import { AppRegistrationSection } from 'AppRegistrationSection';
import { ButtonActor, Modal } from 'components';
import { Frame } from 'components/organisms';
import { frameConfig } from '../../AppConfig';
import { getFetchHeaders } from '../../utils/fetchHeaders';
import { getScreenTranslation } from '../../utils/translations';
import { JUICY_COMPLETE_BTN } from 'utils/constants';
import { PHONE } from 'utils/constants';
import { routes } from 'routes';
import { spacesSanitizer } from '../../utils/sanitizers';
import { useErrorManager } from 'InternalLibrary';
import { useGetClientApplication, usePutProfile } from '../../api';
import { useLocation, useNavigate } from 'react-router-dom';
import useClient from '../../hooks/useClient';

import { useTranslation } from 'react-i18next';
import Card from 'components/atoms/Card/Card';
import CardGroup from 'components/atoms/CardGroup/CardGroup';
import classes from 'classnames';
import classNames from 'classnames';
import IBAN from 'components/molecules/IBAN/IBAN';
import layout from 'scss/layout.module.scss';
import Link from 'components/atoms/Link/Link';
import React, { useCallback, useEffect, useState } from 'react';
import RegistrationLoading from '../RegistrationLoading/RegistrationLoading';
import Spacer from 'components/atoms/Spacer/Spacer';
import styles from './BankAccount.module.scss';
import Text from 'components/atoms/Typography/Text';
import Title from 'components/atoms/Title/Title';
import useAuth from '../../hooks/useAuth';

export default function BankAccount() {
  const { fetch: fetchPutProfile } = usePutProfile();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { setError } = useErrorManager();
  const [loadingClient, setLoadingClient] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingApplication, setLoadingApplication] = useState(true);
  const [saveBtnEnabled, setSaveBtnEnabled] = useState(false);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [isIbanError, setIsIbanError] = useState(false);
  const [validIBAN, setValidIBAN] = useState('');
  const { logout } = useAuth();
  const { client, fetchClient } = useClient();
  const { data: applicationData, fetch: fetchGetApplication } =
    useGetClientApplication();
  const i18nNext = useTranslation('bankAccount');

  const onChangeIBAN = useCallback(
    (inputIBAN: string, isValid: boolean) => {
      if (!isValid && !inputIBAN.match(/_/g)) {
        if (!isIbanError) setIsIbanError(true);
      } else {
        if (isIbanError) setIsIbanError(false);
      }
      if (saveBtnEnabled !== isValid) setSaveBtnEnabled(isValid);
      if (isValid) setValidIBAN(inputIBAN);
    },
    [isIbanError, saveBtnEnabled],
  );

  const navigateNextScreen = () => {
    switch (pathname) {
      case routes.FirstLoan_Manual_Upload_BankAccount:
        navigate(routes.FirstLoan_Manual_Upload_Pending);
        return;
      default:
        navigate(routes.FirstLoan_OnlineBank_Pending);
        return;
    }
  };

  useEffect(() => {
    async function fetchClient() {
      try {
        setLoadingClient(true);
        const clientData = client || (await fetchClient());
        if (clientData?.bankAccount) {
          navigateNextScreen();
          return;
        }
      } catch {
      } finally {
        setLoadingClient(false);
      }
    }

    async function fetchApplication() {
      setLoadingApplication(true);
      try {
        const application =
          applicationData ||
          (await fetchGetApplication({
            headers: getFetchHeaders({
              Accept: 'application/vnd.4finance.web.v1.hal+json',
              'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
            }),
          }));
        const { resolution, resolutionDetail } = application || {};
        if (
          resolution === 'REJECTED' &&
          resolutionDetail === 'LOW_IOVATION_SCORE'
        ) {
          await logout();
        }
      } catch {
      } finally {
        setLoadingApplication(false);
      }
    }

    fetchApplication();

    if (client && client.bankAccount) {
      navigateNextScreen();
      return;
    }

    if (client && !client.bankAccount) {
      setLoadingClient(false);
      return;
    }

    fetchClient();
  }, []);

  const onSubmit = useCallback(async () => {
    try {
      setLoading(true);
      await fetchPutProfile({
        body: {
          bankAccountNumber: spacesSanitizer(validIBAN),
          registrationPhase: 'BANK_ACCOUNT',
        },
      });
      await fetchClient();
      navigateNextScreen();
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [fetchPutProfile, setError, navigateNextScreen, validIBAN]);

  if (loadingClient || loadingApplication) {
    return <RegistrationLoading />;
  }

  const content = getScreenTranslation('bankAccount', i18nNext);

  return (
    <>
      <Frame
        {...frameConfig}
        bodyWrapperClassName={classNames(
          layout['frame-x-compact'],
          layout['width-lg'],
          layout['frame-x-centered'],
        )}
        hideEditButton={true}
        sectionID={AppRegistrationSection.Confirmation}
      >
        <Title text={content.title} dataTest="css-Bank-Account-Title" />
        <Text
          kind="p"
          text={content.subtitle}
          level={2}
          className={classNames(
            styles['helper-color'],
            styles['bank-account-subtitle'],
          )}
          dataTest="css-Bank-Account-Subtitle"
        />
        <CardGroup>
          <Card
            className={classNames(layout['card-compact'], styles['card-1'])}
            dataTest="card-iban"
          >
            <IBAN
              countryISO="ES"
              onChange={onChangeIBAN}
              dataTest="#IBAN"
              analytics={{
                description: 'IBAN',
              }}
            />
            {isIbanError ? (
              <span
                className={classes([styles['helper-text'], styles.error])}
                data-test="error-text"
              >
                {i18nNext.t('error', {
                  phoneNumber: PHONE,
                })}
              </span>
            ) : (
              <span className={styles['helper-text']} data-test="helper-text">
                {content.cardHelper}{' '}
                <Text
                  text="ES91 2100 0418 4502 0005 1332"
                  level={3}
                  weight="bold"
                  dataTest="iban-number"
                />
              </span>
            )}
          </Card>
          <Card
            intent="success"
            className={classNames(layout['card-compact'], styles['card-2'])}
            dataTest="card-success"
          >
            <Text
              text={content.cardGreenTitle}
              level={2}
              dataTest="green-title"
            />
            <Text
              text={'– ' + content.cardGreenMsg1}
              level={2}
              dataTest="green-msg-1"
            />
            <Text
              text={'– ' + content.cardGreenMsg2}
              level={2}
              dataTest="green-msg-2"
            />
            <Text
              text={'– ' + content.cardGreenMsg3}
              level={2}
              dataTest="green-msg-3"
            />
            <Link
              className={styles['iban-info']}
              dataTest="link-iban"
              href="#"
              onClick={() => setIsModalOpened(true)}
            >
              {content.questionLink}
            </Link>
          </Card>
        </CardGroup>
        <ButtonActor
          buttonProps={{
            className: JUICY_COMPLETE_BTN,
            dataTest: 'xpath-Save-And-Continue-Button',
            disabled: !saveBtnEnabled,
            fill: true,
            loading: loading,
            onClick: onSubmit,
            type: 'submit',
          }}
          className={classNames(
            layout['width-sm'],
            layout['frame-x-padding'],
            styles.button,
          )}
        >
          {content.continueBtn}
        </ButtonActor>
      </Frame>
      <Modal
        isOpen={isModalOpened}
        onClose={() => setIsModalOpened(false)}
        className={styles.modal}
      >
        <Title
          forceColumn={true}
          text={content.modalTitle}
          dataTest="title-modal"
        />
        <Spacer paddingTop="30px">
          <Text
            dangerouslySetInnerHTML={{ __html: content.modalText }}
            dataTest="text-modal"
          />
        </Spacer>
      </Modal>
    </>
  );
}
